import {useState} from 'react';

import {SimpleGrid} from '@mantine/core';

import {useProducts} from '../hooks/useProducts.ts';
import {useProductsTableColumns} from '../hooks/useProductsTableColumns.tsx';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
    useTableSort,
} from '@flexinet/ui-components';

import {Product} from '../types';
import {useAuth} from "../../../hooks/useAuth.ts";
import {useAllowedActions} from '../../../hooks/useAllowedActions.ts';
import {FormDrawer} from '../drawers/FormDrawer';
import {update} from '../services/ProductsService';
import {stripReadonlyFields} from '../../../utils/stripReadonlyFields';
import {useQueryClient} from '@tanstack/react-query';
import {I18n} from '@aws-amplify/core';

export const RecordsTable = ({
                                 selectedRecords,
                                 setSelectedRecords,
                             }: {
    selectedRecords: Product[],
    setSelectedRecords: (records: Product[]) => void,
}) => {

    const {allowedActions} = useAllowedActions();
    const queryClient = useQueryClient();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<Product | null>(null);

    const {
        user,
    } = useAuth();

    if (!user) {
        throw new Error('user is required parameter');
    }

    const cognitoUsername = user.username;

    const {
        visibleItems,
        loading,
        nextToken,
        isFetchingMore,
        fetchRecords,
        handleFilterByKeyword,
    } = useProducts();

    // Newest first. `created` is the only creation timestamp Product has; it is
    // nullable in the schema, so rows without it sort last in both directions.
    // Under token pagination this sorts only the pages fetched so far, not the
    // whole dataset: a property of the model, not a bug.
    const {sortStatus, setSortStatus, sorted} = useTableSort<Product>(visibleItems, {
        columnAccessor: 'created',
        direction: 'desc',
    });

    const handleRecordClick = (record: Product) => {
        setSelectedRecord(record);
        setDrawerOpened(true);
    };

    const columns = useProductsTableColumns(handleRecordClick);

    const handleDrawerSubmit = async (values: Product) => {
        if (selectedRecord?.id) {
            await update(selectedRecord.id, stripReadonlyFields(values) as Product);
            queryClient.invalidateQueries({queryKey: ['products']});
        }
    };

    // Only the creator or manager of a product may select it for deletion.
    const selectionProps = allowedActions.includes('DeleteProduct') ? {
        selectedRecords,
        onSelectedRecordsChange: setSelectedRecords,
        isRecordSelectable: (item: Product) =>
            item.createdByUsername === cognitoUsername || item.managedByUsername === cognitoUsername,
    } : {};

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            <MantineDataTable<Product>
                verticalSpacing='sm'
                striped
                highlightOnHover
                minHeight={150}
                records={sorted}
                columns={columns}
                fetching={loading}
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                noRecordsText={I18n.get('No records found')}
                {...selectionProps}
            />

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                nextToken={nextToken}
                handleLoadMore={fetchRecords}
                isFetchingMore={isFetchingMore}
                noun='product'
            />

            <FormDrawer
                record={selectedRecord || {} as Product}
                handleSubmit={handleDrawerSubmit}
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
            />
        </SimpleGrid>
    );
};
